<template>
	<div>
		<app-app-header
			:title="mobileHeaderData.title"
			:color="$config.public.colors.primary"
			:breadcrumbs="mobileHeaderData.breadcrumbs"
			:item-link="mobileHeaderData.itemLink"
			v-model="indexStore.isHeaderVisible"
			flat-on-top/>
		<app-content>
			<page-content xs-full sm-full class="sm:bg-gray-100 pb-24">
				<container xs-full sm-full class="stretch-layout">
					<fetch-loader v-if="pending || error" :pending="pending" :error="error" class="stretch-layout"/>
					<row v-else-if="$auth.loggedIn && manageStore.user">
						<column cols="12" md="3" class="none md:block">
							<sticky-content>
								<card>
									<card-title spaced-content>{{ manageStore.user.display_name }}</card-title>

									<div class="py-8">
										<tabs vertical>
											<template v-for="(tabChildRoute, tabKey) in tabRoutes" :key="tabKey">
												<tab :exact="tabChildRoute.exact"
													 :to="tabChildRoute.to"
													 replace>
													<div class="flex items-center justify-between w-full">
														<span>{{ tabChildRoute.text }}</span>

														<badge v-if="tabChildRoute.badge" rounded spaced small color="secondary">{{ tabChildRoute.badge }}</badge>
													</div>
												</tab>
												<template v-if="tabChildRoute.tabs?.length && $route.path.startsWith(tabChildRoute.to)">
													<template v-for="(tabSubRoute, tabSubRouteKey) in tabChildRoute.tabs" :key="`${tabKey}-${tabSubRouteKey}`">
														<tab :exact="tabSubRoute.exact"
															 :height="40"
															 class="pl-40"
															 :to="tabSubRoute.to"
															 replace>
															{{ tabSubRoute.text }}
														</tab>

														<template v-if="tabSubRoute.tabs?.length && $route.path.startsWith(tabSubRoute.to)"
																  :key="`${tabKey}-${tabSubRouteKey}-${tabSubSubRouteKey}`">
															<tab v-for="(tabSubSubRoute, tabSubSubRouteKey) in tabSubRoute.tabs" :exact="tabSubSubRoute.exact"
																 :height="34"
																 class="pl-48"
																 :to="tabSubSubRoute.to"
																 replace>
																{{ tabSubSubRoute.text }}
															</tab>
														</template>
													</template>
												</template>
											</template>

										</tabs>
									</div>

									<card-buttons stretch class="border-t pt-16">
										<btn :to="$web('users.show', $auth.user?._key ?? 'my')">{{ $t('action.show_profile') }}</btn>
									</card-buttons>
								</card>
							</sticky-content>
						</column>
						<column cols="12" md="9">
							<div class="overflow-x-hidden md:m--8 md:p-8">
								<NuxtPage/>
							</div>
						</column>
					</row>
					<login-to-see-more v-else class="min-h-200"/>
				</container>
			</page-content>
		</app-content>
	</div>
</template>

<script setup lang="ts">

import {useIndexStore} from "../../../store";
import {useManageStore} from "../../../store/manage";

const indexStore = useIndexStore();
const manageStore = useManageStore();
const {$api, $web, $auth} = useNuxtApp();
const {t} = useI18n();
const route = useRoute();

const { pending, error } = await useAsyncData('manage', () => manageStore.refreshUser(), {
		server: false,
		immediate: $auth.initialized,
		watch: [() => $auth.initialized]
	})

const tabRoutes = computed(() => {
	const tabs: {[key: string]: any}[] = [
		{to: $web('my.profile.manage.index'), text: t('label.overview'), exact: true},
		{to: $web('my.profile.manage.data'), text: t('label.base_data')},
		{to: $web('my.profile.manage.contact'), text: t('label.contact_data')},
		{to: $web('my.profile.manage.description'), text: t('label.profile_description')},
		{to: $web('my.profile.manage.sport_types'), text: t('label.sport_types')},
		{to: '/my/profile/manage/connections', text: t('label.connections')},
		{to: '/my/profile/manage/links', text: t('label.links')},
	];

	if (manageStore.user && (!manageStore.user.age || manageStore.user.age >= 18)) {
		tabs.push({to: $web('my.profile.manage.children'), text: t('label.children')});
	}
	if (manageStore.user && manageStore.user.age && manageStore.user.age < 18) {
		tabs.push({to: $web('my.profile.manage.parents'), text: t('label.parents')});
	}

	tabs.push({to: $web('my.profile.manage.articles.index'), text: t('label.blog'), badge: t('label.new')});
	tabs.push({to: $web('my.profile.manage.billing_requests.index'), text: t('label.billing_requests')});

	tabs.push({to: $web('my.profile.manage.password'), text: t('label.password')});
	tabs.push({to: $web('my.profile.manage.payment_information'), text: t('label.payment_methods')});
	tabs.push({to: $web('my.profile.manage.bank_accounts'), text: t('label.bank_accounts')});
	tabs.push({
		to: $web('my.profile.manage.settings'), text: t('label.settings'), tabs: [
			{text: t('label.privacy'), to: $web('my.profile.manage.settings.privacy')},
			{
				text: t('label.notifications'), to: $web('my.profile.manage.settings.notifications.index'), tabs: [
					{text: t('label.general'), to: $web('my.profile.manage.settings.notifications.general')},
					{text: t('label.athletes'), to: $web('my.profile.manage.settings.notifications.athletes')},
					{text: t('label.clubs_teams'), to: $web('my.profile.manage.settings.notifications.clubs')},
					{text: t('label.events'), to: $web('my.profile.manage.settings.notifications.events')},
					{text: t('label.devices'), to: $web('my.profile.manage.settings.notifications.devices')},
				]
			},
		]
	});

	return tabs;
});

const mobileHeaderData = computed(() => {
	const subPages = [];

	// findLast is not supported in node yet
	const mainTabRoute = [...tabRoutes.value].reverse().find((tab) => {
		return route.path.startsWith(tab.to);
	});
	if (mainTabRoute) {
		subPages.push(mainTabRoute);

		if (mainTabRoute.tabs) {
			mainTabRoute.tabs.forEach((subTab) => {
				if (route.path.startsWith(subTab.to)) {
					subPages.push(subTab);
				}
				if (subTab.tabs) {
					subTab.tabs.forEach((subSubTab) => {
						if (route.path.startsWith(subSubTab.to)) {
							subPages.push(subSubTab);
						}
					});
				}
			});
		}
	}

	return {
		title: subPages.length ? subPages[subPages.length - 1].text : t('action.manage_profile'),
		breadcrumbs: [subPages.length ? {to: $web('my.profile.manage.index'), text: t('action.manage_profile')} : null, ...(subPages.length > 1 ? subPages.slice(0, -1) : [])].filter(Boolean),
		itemLink: {to: $web('my.profile.index'), text: t('action.show_profile')},
	}
});

useMeta({
	title: t('view.my.profile.manage.index.title'),
	description: t('view.my.profile.manage.index.description'),
	noIndex: true
})

</script>

